import { useForm } from '@mantine/form';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Paper,
  Text,
  Group,
} from '@mantine/core';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { IconAlertTriangle, IconInfoCircle, IconMessageCircleQuestion } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useSaveMutation } from '@/services/api';
import { updateExtraDetails } from '@/data/features/proposal/proposal';
import validate from '@/helpers/validations';
import { getMutationResult } from '@/helpers/query';
import { Page, ProcessHeader, YesNoToggle } from '@/components';
import Modal from '@/components/Modal';
import { getRouteById, routeWithParams } from '@/helpers/routes';
import routes from '@/routes';
import { useIdInStoreGuard } from '@/hooks/useIdfromUrl';
import ProcessButton from '@/components/ProcessButton';
import SendDetailsModal from '@/components/SendDetailsModal';
import { PROBLEM_TYPES } from '@/helpers/enums';
import ChequeIcon from '@/assets/images/cheque.svg?react'; // eslint-disable-line import/no-unresolved
import classes from './styles.module.css';

const BankingConduct = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [showBlockingModal, setShowBlockingModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const dispatch = useDispatch();
  const [apiSave] = useSaveMutation();
  const navigate = useNavigate();
  const { extraDetails, id } = useSelector((state) => state.proposal);
  useIdInStoreGuard();

  const required = t('globals.validations.required');

  const formValidations = {
    returnedChecks: (value) => validate(value, { required }),
    standingOrderReturn: (value) => validate(value, { required }),
    bankAccountRestriction: (value) => validate(value, { required }),
    enforcementFile: (value) => validate(value, { required }),
  };

  const {
    loanTypeName,
  } = extraDetails;

  const initialValues = {
    returnedChecks: extraDetails.returnedChecks,
    standingOrderReturn: extraDetails.standingOrderReturn,
    bankAccountRestriction: extraDetails.bankAccountRestriction,
    enforcementFile: extraDetails.enforcementFile,
  };

  const form = useForm({
    mode: 'controlled',
    validateInputOnBlur: true,
    initialValues,
    validate: formValidations,
    validateInputOnSubmit: true,
  });

  const handleContinue = async () => {
    try {
      setLoading(true);

      // Validate form
      const validation = form.validate();
      if (validation.hasErrors) {
        return;
      }

      // Save data to API
      const objectToSend = {
        ...form.values,
        id,
      };

      const { isError } = await getMutationResult(objectToSend, apiSave);

      if (isError) {
        notifications.show({
          title: t('globals.error.title'),
          message: t('globals.error.message'),
        });
        return;
      }

      // Check if any question has "yes" answer
      const hasYesAnswer = Object.values(form.values).some((value) => value === 'yes');
      if (hasYesAnswer) {
        setShowBlockingModal(true);
        setLoading(false);
        return;
      }

      // Save data to store
      dispatch(updateExtraDetails(form.values));

      if (loanTypeName === 'reverseMortgage') {
        navigate(routeWithParams(getRouteById(routes.newProposal.id, 'reverseMortgageRouteType').path, { id }));
        return;
      }

      const routeMap = {
        mortgageRefinancing: 'uploadBankFile',
        reverseMortgage: 'reverseMortgageRouteType',
      };

      const routeKey = routeMap[loanTypeName] || 'incomeLiabilities';
      navigate(routeWithParams(getRouteById(routes.newProposal.id, routeKey).path, { id }));
    } catch (error) {
      console.error('Error saving banking conduct data:', error);
      notifications.show({
        title: t('globals.error.title'),
        message: t('globals.error.message'),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReturn = () => {
    if (loanTypeName === 'mortgageRefinancing') {
      navigate(routeWithParams(getRouteById(routes.newProposal.id, 'reason').path, { id }));
    } else {
      // Navigate to next step
      navigate(routeWithParams(getRouteById(routes.newProposal.id, 'assetValue').path, { id }));
    }
  };

  const questions = [
    {
      field: 'returnedChecks',
      question: 'האם הוחזרו ללקוחות 3 צ\'קים ויותר במהלך שלושת השנים האחרונות?',
      label: 'החזרת צ\'קים',
    },
    {
      field: 'standingOrderReturn',
      question: 'האם חזרה ללקוחות הוראת קבע במהלך שלושת השנים האחרונות?',
      label: 'החזרת הוראת קבע',
    },
    {
      field: 'bankAccountRestriction',
      question: 'האם הוגבל אחד מחשבונות הבנק של הלקוחות במהלך שלושת השנים האחרונות?',
      label: 'הגבלת חשבון בנק',
    },
    {
      field: 'enforcementFile',
      question: 'האם נפתח נגד הלקוחות תיק בהוצאה לפועל במהלך שלושת השנים האחרונות?',
      label: 'תיק בהוצאה לפועל',
    },
  ];

  return (
    <ProcessHeader
      headerText="התנהלות בנקאית"
      headerColor="#FFE6F0"
      headerTextColor="#E91E63"
      showReturnButton
      onReturn={handleReturn}
      icon={ChequeIcon}
    >
      <form onSubmit={form.onSubmit(handleContinue)}>
        {questions.map((q) => (
          <Paper key={q.field}>
            <YesNoToggle
              name={q.field}
              label={q.question}
              value={form.values[q.field]}
              yesColor="#E91E63"
              noColor="#4CAF50"
              onChange={(value) => form.setFieldValue(q.field, value)}
              required
              error={form.errors[q.field]}
            />
          </Paper>
        ))}
        <Page.Actions>
          <ProcessButton type="submit" loading={loading} />
        </Page.Actions>
      </form>

      {/* Blocking Modal for "Yes" answers */}
      <Modal
        size='600'
        opened={showBlockingModal}
        close={() => setShowBlockingModal(false)}
        title={
          <Group gap="xs" align="center">
            <IconAlertTriangle size={24} color="#E91E63" />
            <Text fw={600} size="lg" c="#E91E63">
              זה קורה במשפחות הכי טובות!
            </Text>
          </Group>
        }
        footer={
          <Group justify="space-between" w="100%" gap="sm">
            <Button
              variant="light"
              onClick={() => {
                setShowBlockingModal(false);
                setShowDetailsModal(true);
              }}
              rightSection={<IconMessageCircleQuestion size={16} />}
            >
              העבר ליועץ CreditPro
            </Button>
            <Button
              variant="filled"
              onClick={() => setShowBlockingModal(false)}
              leftSection={<IconInfoCircle size={16} />}
              styles={{ root: { backgroundColor: '#E91E63' } }}
            >
              הבנתי את הבעיה
            </Button>
          </Group>
        }
      >
        <Group gap="md" align="flex-start" mb="xl">
          <IconInfoCircle size={32} color="#E91E63" style={{ marginTop: 4 }} />
          <div style={{ flex: '1' }}>
            <Text size="md" fw={500} mb="xs" c="#333">
              ההתנהלות הבנקאית של הלקוח איננה מושלמת.
            </Text>
            <Text size="sm" c="#666" lh={1.6}>
              לא נורא!
              <br />
              יש לנו פתרונות גם למקרים כאלה.
              <br />
              <br />
              <strong>המלצות:</strong>
              <Text/>
              <ul style={{ padding: '0', margin: '0', marginRight: '0.7rem' }} className={classes.customBullets}>
                <li style={{ marginLeft: 0 }}><Text size="sm" c="#666" lh={1.6}>באפשרותך לחזור ולוודא שוב מול הלקוח.</Text></li>
                <li style={{ marginLeft: 0 }}><Text size="sm" c="#666" lh={1.6}>לחילופין, באפשרותך להעביר את הפניה ליועץ קרדיט פרו לבחינת האפשרויות המתאימות ללקוח.</Text></li>
              </ul>
            </Text>
          </div>
        </Group>
      </Modal>

      <SendDetailsModal
        opened={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        data={{ bankingConduct: form.values, type: PROBLEM_TYPES.bankingConduct }}
      />
    </ProcessHeader>
  );
};

export default BankingConduct;
